import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';

import { inactiveSelectionColor } from '@aline/common';
import { flip, offset, shift } from '@floating-ui/react';
import { Box, Portal } from '@mui/material';
import {
  BoldPlugin,
  HeadingPlugin,
  ItalicPlugin,
  StrikethroughPlugin,
  UnderlinePlugin,
} from '@platejs/basic-nodes/react';
import { useFloatingToolbar, useFloatingToolbarState } from '@platejs/floating';
import { CursorOverlayPlugin, useCursorOverlay } from '@platejs/selection/react';
import { TrailingBlockPlugin } from '@platejs/utils';
import clsx from 'clsx';
import { isHotkey } from 'is-hotkey';
import { HtmlPlugin } from 'platejs';
import {
  Plate,
  PlateContainer,
  PlateContent,
  useEditorId,
  useEventEditorValue,
  usePlateEditor,
} from 'platejs/react';
import { useMount } from 'react-use';
import { getTypographyForSx } from 'src/theme';
import { makeStyles } from 'tss-react/mui';
import { useDebouncedCallback } from 'use-debounce';

import { AlineMarkdownEditorToolbar } from './AlineMarkdownEditorToolbar';
import { CommandPlaceholderProvider, renderEditorPlaceholder } from './editor-placeholder';
import { EmptyParagraphActionsProvider } from './empty-paragraph-actions';
import { LinkKit } from './plugins/LinkKit';
import { ListKit } from './plugins/ListKit';
import { BaseMarkdownPlugin } from './plugins/MarkdownPlugin';
import {
  MentionElementPlugin,
  MentionEntitiesContext,
  MentionEntityKit,
} from './plugins/mention-entity-kit';
import { ParagraphPlugin } from './plugins/ParagraphPlugin';
import { TableKit } from './plugins/TableKit';
import type { AlineMarkdownEditorProps, AlineMarkdownEditorRef } from './types';
import { focusEditor } from './utils/focus';
import { getEditorMarkdown } from './utils/markdown';
import { openMentionCombobox } from './utils/open-mention-combobox';
import { getFormatToolbarStyles } from './utils/styles';
import {
  deserializeMarkdownWithMentions,
  getIsEditorWithMarkdownApi,
  getMarkdownHasMentionType,
} from '../ChatEditor';

const useEditorStyles = makeStyles()(theme => ({
  container: {
    '&:focus-within': {
      border: `1px solid ${theme.palette.primary.main}`,
    },
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 20,
    boxSizing: 'border-box',
    overflow: 'hidden',
    width: '100%',
  },
  floatingToolbarWrap: getFormatToolbarStyles(theme),
  textarea: {
    ...getTypographyForSx(theme.typography.body1),
    backgroundColor: 'transparent',
    border: 'none',
    boxSizing: 'border-box',
    color: theme.appColors.iconsType,
    display: 'block',
    margin: 0,
    outline: 'none',
    overflow: 'auto',
    padding: theme.spacing(1),
    resize: 'none',
    width: '100%',
  },
}));

/**
 * Simple, all-inclusive markdown editor component.
 * Provides a toolbar with all formatting buttons and a text area.
 */
export const AlineMarkdownEditor = forwardRef<AlineMarkdownEditorRef, AlineMarkdownEditorProps>(
  function AlineMarkdownEditor(
    {
      autoFocus = false,
      bottomContent,
      className,
      commandPlaceholder = false,
      dataCy,
      disabled = false,
      extraPlugins = [],
      maxHeight = 300,
      maxWidth,
      mentions,
      minHeight = 80,
      noBorder = false,
      onBlur,
      onChange,
      onFocus,
      onKeyDown,
      onPaste,
      onSubmit,
      placeholder = 'Type something...',
      readOnly = false,
      style,
      submitDisabled = false,
      textareaStyle,
      toolbarStyle,
      topContent,
      value,
      width,
    },
    ref
  ) {
    const { classes } = useEditorStyles();
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    /**
     * The plugin list keys off a boolean (not the mentions object) so inline config
     * props do not recreate the editor every render; the config itself flows through
     * MentionEntitiesContext below.
     */
    const mentionsEnabled = Boolean(mentions);
    const plugins = useMemo(
      () => [
        HtmlPlugin,
        ParagraphPlugin,
        BoldPlugin,
        ItalicPlugin,
        UnderlinePlugin,
        StrikethroughPlugin,
        HeadingPlugin,
        ...ListKit,
        ...LinkKit,
        ...TableKit,
        /**
         * Mention chips render in every editor so persisted mention links stay visible;
         * the @-trigger input and entity picker mount only when mentions are enabled.
         */
        MentionElementPlugin,
        ...(mentionsEnabled ? MentionEntityKit : []),
        // Cursor overlay for selection preservation (always rendered for consistent behavior).
        CursorOverlayPlugin.configure({
          render: {
            afterEditable: () => <CursorOverlay />,
          },
        }),
        // Trailing block plugin - ensures there's always a paragraph at the end.
        TrailingBlockPlugin.configure({
          options: {
            type: 'p',
          },
        }),
        // Markdown plugin (must come last for proper serialization).
        BaseMarkdownPlugin,
        // Extra plugins overriding base plugins.
        ...extraPlugins,
      ],
      [extraPlugins, mentionsEnabled]
    );
    const editor = usePlateEditor({
      plugins,
      value: [{ children: [{ text: '' }], type: 'p' }],
    });
    const handleEditorChange = useDebouncedCallback(
      () => onChange?.(getEditorMarkdown(editor)),
      100
    );

    /**
     * Flush the pending onChange on unmount so the last sub-100ms of typing isn't dropped when the
     * editor is torn down (e.g. switching tasks / closing a panel mid-keystroke).
     */
    useEffect(() => () => handleEditorChange.flush(), [handleEditorChange]);

    /**
     * `value` is the text the editor opens with, loaded once. A tab hidden with React Activity
     * runs its effects again when shown, and loading `value` again then replaced what the author
     * typed since, such as an edit to a comment, with the text the editor opened with.
     */
    const valueLoadedRef = useRef(false);
    useMount(() => {
      if (valueLoadedRef.current) return;
      valueLoadedRef.current = true;
      if (value && getIsEditorWithMarkdownApi(editor))
        editor.tf.setValue(deserializeMarkdownWithMentions(editor, value));
    });

    useImperativeHandle(ref, () => ({
      clear: () => {
        editor.tf.reset();
        focusEditor(editor, 'start');
      },
      focus: (position = 'preserve') => {
        focusEditor(editor, position);
      },
      getEditor: () => editor,
      getHasMentionType: mentionTypeName =>
        getIsEditorWithMarkdownApi(editor)
          ? getMarkdownHasMentionType(editor, getEditorMarkdown(editor), mentionTypeName)
          : false,
      getMarkdown: () => getEditorMarkdown(editor),
      getScrollContainer: () => scrollContainerRef.current,
      insertText: (text: string) => {
        editor.tf.insertText(text);
        focusEditor(editor, 'end');
      },
      openMentionPicker: () => openMentionCombobox(editor),
      setMarkdown: (markdown: string) => {
        if (getIsEditorWithMarkdownApi(editor))
          editor.tf.setValue(deserializeMarkdownWithMentions(editor, markdown));
      },
    }));

    const handleKeyDown = (e: React.KeyboardEvent) => {
      onKeyDown?.(e);
      if (e.defaultPrevented) return;

      if (isHotkey('mod+enter', e.nativeEvent) && onSubmit && !submitDisabled) {
        e.preventDefault();
        onSubmit(getEditorMarkdown(editor));
      }
    };

    const containerStyle: React.CSSProperties = {
      ...(width !== undefined && { width }),
      ...(maxWidth !== undefined && { maxWidth }),
      ...(noBorder && { border: 'none', borderRadius: 0 }),
      ...style,
    };

    return (
      <MentionEntitiesContext.Provider value={mentions ?? null}>
        <EmptyParagraphActionsProvider value={!readOnly && !disabled}>
          <CommandPlaceholderProvider value={commandPlaceholder}>
            {/*
             * With a footer inside the card, the card is what the `@` and `/` menus open
             * from, so they never cover the footer. The chat composer sets its own anchor.
             */}
            <Box
              className={clsx(classes.container, className)}
              data-editor-picker-anchor={bottomContent ? '' : undefined}
              style={containerStyle}
            >
              <Plate editor={editor} onChange={handleEditorChange}>
                {!readOnly && !disabled && (
                  <FloatingFormatToolbar className={classes.floatingToolbarWrap}>
                    <AlineMarkdownEditorToolbar style={toolbarStyle} />
                  </FloatingFormatToolbar>
                )}
                <PlateContainer>
                  {topContent}
                  <PlateContent
                    ref={scrollContainerRef}
                    autoFocus={autoFocus}
                    className={classes.textarea}
                    data-cy={dataCy}
                    placeholder={placeholder}
                    readOnly={readOnly || disabled}
                    renderPlaceholder={renderEditorPlaceholder}
                    style={{
                      maxHeight,
                      minHeight,
                      opacity: disabled ? 0.6 : 1,
                      ...textareaStyle,
                    }}
                    onBlur={onBlur}
                    onFocus={onFocus}
                    onKeyDown={handleKeyDown}
                    onPaste={onPaste}
                  />
                </PlateContainer>
              </Plate>
              {bottomContent}
            </Box>
          </CommandPlaceholderProvider>
        </EmptyParagraphActionsProvider>
      </MentionEntitiesContext.Provider>
    );
  }
);

/** Shows selection highlight when editor loses focus. */
function CursorOverlay() {
  const { cursors } = useCursorOverlay();

  return (
    <>
      {cursors.flatMap(({ caretPosition, id, selectionRects }) => [
        ...selectionRects.map((rect, i) => (
          <div
            key={`${id}-${i}`}
            style={{
              background: inactiveSelectionColor.toString(),
              pointerEvents: 'none',
              position: 'absolute',
              zIndex: 10,
              ...rect,
            }}
          />
        )),
        caretPosition && (
          <div
            key={`${id}-caret`}
            style={{
              background: inactiveSelectionColor.toString(),
              pointerEvents: 'none',
              position: 'absolute',
              width: 2,
              zIndex: 10,
              ...caretPosition,
            }}
          />
        ),
      ])}
    </>
  );
}

/** Formatting actions anchored to the active editor's text selection. */
function FloatingFormatToolbar({
  children,
  className,
}: {
  children: React.ReactNode;
  className: string;
}): JSX.Element | null {
  const editorId = useEditorId();
  const focusedEditorId = useEventEditorValue('focus');
  const state = useFloatingToolbarState({
    editorId,
    floatingOptions: {
      middleware: [offset(8), flip({ padding: 12 }), shift({ padding: 12 })],
      placement: 'top-start',
      strategy: 'fixed',
    },
    focusedEditorId,
  });
  const { clickOutsideRef, hidden, props, ref } = useFloatingToolbar(state);
  if (hidden) return null;
  return (
    <Portal>
      <div ref={clickOutsideRef}>
        <div
          ref={ref}
          {...props}
          aria-label="Text formatting"
          className={className}
          role="toolbar"
          onMouseDown={event => event.preventDefault()}
        >
          {children}
        </div>
      </div>
    </Portal>
  );
}
